import React, { useState, useEffect } from 'react';
import { Mail, Linkedin, Instagram, ArrowUpRight } from 'lucide-react';
import axios from 'axios';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import Reveal from '../components/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [founder, setFounder] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [founderRes, faqsRes, contactRes] = await Promise.all([
          axios.get(`${API}/founder`),
          axios.get(`${API}/faqs`),
          axios.get(`${API}/contact`),
        ]);

        setFounder(founderRes.data);
        setFaqs(faqsRes.data);
        setContactInfo(contactRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="dark-container min-h-screen flex items-center justify-center" data-testid="contact-loading">
        <p className="text-white">Loading...</p>
      </div>
    );
  }

  const channels = [
    {
      icon: Mail,
      label: 'Email',
      value: contactInfo?.email_display || 'hello@deftexperiences.com',
      href: `mailto:${contactInfo?.email || 'hello@deftexperiences.com'}`,
      testid: 'contact-email-link',
      external: false,
    },
    {
      icon: Linkedin,
      label: 'LinkedIn',
      value: contactInfo?.linkedin_display || 'Deft Experiences',
      href: contactInfo?.linkedin_url || 'https://www.linkedin.com/company/deft-experiences/',
      testid: 'contact-linkedin-link',
      external: true,
    },
    {
      icon: Instagram,
      label: 'Instagram',
      value: contactInfo?.instagram_display || '@deft_experiences',
      href: contactInfo?.instagram_url || 'https://www.instagram.com/deft_experiences',
      testid: 'contact-instagram-link',
      external: true,
    },
  ];

  return (
    <div className="dark-container" data-testid="contact-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
        <div className="absolute top-0 left-1/4 w-[600px] h-[500px] bg-radial-teal opacity-50 pointer-events-none" />
        <div className="dark-content-container relative pt-20 pb-24 lg:pt-28 lg:pb-32 text-center">
          <span className="eyebrow mb-6 block animate-fade-in">Reach out</span>
          <h1 className="display-huge text-white mb-6 animate-fade-in-delay-1" data-testid="contact-title">
            Get In <span className="text-[#64C8FF]">Touch</span>
          </h1>
          <p className="body-large text-white/65 max-w-2xl mx-auto animate-fade-in-delay-2">
            Have questions or want to connect? We're here to help you join our community of
            ambitious individuals.
          </p>
        </div>
      </section>

      {/* CHANNELS + FOUNDER */}
      <section className="relative">
        <div className="dark-content-container pb-24 lg:pb-36">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Channels */}
            <div className="lg:col-span-7 space-y-8">
              <div>
                <span className="eyebrow mb-3 block">Channels</span>
                <h2 className="display-medium text-white mb-8">Connect With Us</h2>
                <div className="space-y-3">
                  {channels.map((c, idx) => {
                    const Icon = c.icon;
                    return (
                      <Reveal key={c.label} delay={idx * 90}>
                        <a
                          href={c.href}
                          target={c.external ? '_blank' : undefined}
                          rel={c.external ? 'noopener noreferrer' : undefined}
                          className="group flex items-center gap-5 p-5 surface-2 border border-white/8 rounded-2xl dark-transition hover:border-[#00FFD1]/30 hover:bg-white/[0.04]"
                          data-testid={c.testid}
                        >
                          <div className="w-12 h-12 rounded-xl glass-teal flex items-center justify-center flex-shrink-0">
                            <Icon className="text-[#00FFD1]" size={20} />
                          </div>
                          <div className="flex-1">
                            <p className="body-small text-white/50 mb-0.5">{c.label}</p>
                            <p className="body-medium text-white font-medium">{c.value}</p>
                          </div>
                          <ArrowUpRight
                            className="text-white/30 group-hover:text-[#00FFD1] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                            size={20}
                          />
                        </a>
                      </Reveal>
                    );
                  })}
                </div>
              </div>

              {/* Register CTA */}
              <Reveal delay={150} className="relative overflow-hidden glass-teal rounded-3xl p-8 md:p-10">
                <div className="absolute inset-0 bg-grid-pattern opacity-50 pointer-events-none" />
                <div className="relative">
                  <h3 className="heading-1 text-white mb-3">Ready to Join</h3>
                  <p className="body-medium text-white/75 mb-6 max-w-lg">
                    Register your interest to become part of our curated community and gain access
                    to all events and The Nerd Feed
                  </p>
                  <a
                    href={contactInfo?.register_url || 'https://deftexp.fillout.com/lessgo'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex"
                    data-testid="contact-register-btn"
                  >
                    Register Your Interest
                    <ArrowUpRight size={18} />
                  </a>
                </div>
              </Reveal>
            </div>

            {/* Founder */}
            <div className="lg:col-span-5">
              {founder && (
                <Reveal delay={200} className="surface-2 border border-white/8 rounded-3xl overflow-hidden lg:sticky lg:top-28" data-testid="founder-card">
                  <div className="relative h-72">
                    <img
                      src={founder.image}
                      alt={founder.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                    <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full glass-panel text-xs font-semibold tracking-[0.2em] uppercase text-[#00FFD1]">
                      Founder
                    </span>
                  </div>
                  <div className="p-8">
                    <h3 className="heading-1 text-white mb-3">{founder.name}</h3>
                    <p className="body-medium text-white/70 mb-6">{founder.bio}</p>
                    <div className="flex gap-3">
                      <a
                        href={founder.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="LinkedIn"
                        className="w-11 h-11 rounded-full glass-panel flex items-center justify-center text-white/70 hover:text-[#00FFD1] hover:border-[#00FFD1]/40 transition-all"
                        data-testid="founder-linkedin"
                      >
                        <Linkedin size={18} />
                      </a>
                      <a
                        href={founder.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Social"
                        className="w-11 h-11 rounded-full glass-panel flex items-center justify-center text-white/70 hover:text-[#00FFD1] hover:border-[#00FFD1]/40 transition-all"
                        data-testid="founder-social"
                      >
                        <Instagram size={18} />
                      </a>
                    </div>
                  </div>
                </Reveal>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="relative surface-1 border-t border-white/5">
          <div className="absolute inset-0 bg-grid-pattern-fine opacity-50 pointer-events-none" />
          <div className="dark-content-container relative py-24 lg:py-36">
            <div className="max-w-4xl mx-auto">
              <Reveal className="text-center mb-14">
                <span className="eyebrow mb-4 block">FAQ</span>
                <h2 className="display-large text-white">Frequently Asked Questions</h2>
              </Reveal>
              <Accordion type="single" collapsible className="border-t border-white/8">
                {faqs.map((faq) => (
                  <AccordionItem
                    key={faq.id}
                    value={faq.id}
                    className="border-b border-white/8"
                    data-testid={`faq-item-${faq.id}`}
                  >
                    <AccordionTrigger className="hover:no-underline py-7 group [&>svg]:h-6 [&>svg]:w-6 [&>svg]:text-white/40 [&[data-state=open]>svg]:text-[#00FFD1]">
                      <span className="heading-1 text-white text-left pr-6 group-hover:text-[#00FFD1] transition-colors">
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="body-large text-white/65 pb-6 pr-12 leading-relaxed">
                        {faq.answer}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Contact;
